import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Download, MessageCircle, CheckCircle2, Quote, AlertTriangle,
  Target, Wrench, Trophy, Unlock,
} from "lucide-react";
import ScoreGauge from "@/components/funnel/ScoreGauge";
import PlatformChart from "@/components/funnel/PlatformChart";
import TrendChart from "@/components/funnel/TrendChart";
import LockGateModal from "@/components/funnel/LockGateModal";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/anim/Reveal";
import Counter from "@/components/anim/Counter";
import Tilt3D from "@/components/anim/Tilt3D";
import { pdfUrl } from "@/lib/api";
import { HE } from "@/lib/i18n";
import { t } from "@/lib/strings";

const PREVIEW_SECONDS = 5;

const impactLabel = (v) => (HE ? ({ High: "גבוה", Medium: "בינוני", Low: "נמוך" }[v] || v) : v);

const MetricTile = ({ label, value }) => (
  <Tilt3D max={12} className="h-full">
    <div className="av-card av-card-hover p-4 flex flex-col justify-between h-full">
      <span className="av-label">{label}</span>
      <span className="font-data text-3xl font-bold text-obsidian mt-2">
        <Counter to={value} /><span className="text-lg text-neutral-400">%</span>
      </span>
    </div>
  </Tilt3D>
);

const SectionLabel = ({ children }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="h-3 w-3 bg-conversion" />
    <span className="av-label text-obsidian">{children}</span>
  </div>
);

const SnippetCard = ({ s }) => (
  <div className="av-card av-card-hover p-5">
    <div className="flex items-center justify-between mb-2">
      <span className="av-label">{s.platform}</span>
      <span
        className="font-data text-[10px] font-bold uppercase px-2 py-0.5 border-2"
        style={{ color: s.cited ? "#00C853" : "#FF1744", borderColor: s.cited ? "#00C853" : "#FF1744" }}
      >
        {s.cited ? t("results.cited") : t("results.notCited")}
      </span>
    </div>
    <p className="font-data text-xs text-neutral-500 mb-2">“{s.query}”</p>
    <p className="text-sm text-neutral-800 leading-relaxed">{s.snippet}</p>
  </div>
);

const ReportHeader = ({ domain }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ type: "spring", stiffness: 300, damping: 30 }}
    className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10"
  >
    <div>
      <SectionLabel>{t("results.live")}</SectionLabel>
      <h2 className="font-display text-3xl sm:text-4xl text-obsidian">
        {t("results.title")}
      </h2>
      <p className="font-data text-sm text-neutral-600 mt-1" dir="ltr" data-testid="result-domain">{domain}</p>
    </div>
    <div className="flex items-center gap-2 border-2 border-data-positive px-3 py-2 self-start">
      <CheckCircle2 className="h-4 w-4 text-data-positive" />
      <span className="font-data text-xs uppercase tracking-widest text-obsidian">{t("results.scanComplete")}</span>
    </div>
  </motion.div>
);

const UnlockedBanner = ({ leadId }) => (
  <motion.div
    id="full-report"
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-obsidian text-white p-6 md:p-8 mb-8 flex flex-col md:flex-row items-center justify-between gap-4 scroll-mt-16"
    data-testid="report-unlocked-banner"
  >
    <div className="flex items-center gap-4">
      <div className="bg-[#25D366] p-3"><MessageCircle className="h-6 w-6 text-obsidian" /></div>
      <div>
        <h3 className="font-heading text-xl font-bold">{t("results.bannerTitle")}</h3>
        <p className="text-sm text-neutral-400">{t("results.bannerSub")}</p>
      </div>
    </div>
    <a
      href={pdfUrl(leadId)}
      target="_blank"
      rel="noreferrer"
      className="bg-conversion text-white font-bold uppercase tracking-wide px-6 py-3 flex items-center gap-2 hover:bg-conversion-hover transition-colors shrink-0"
      data-testid="download-pdf-btn"
    >
      <Download className="h-5 w-5" /> {t("results.download")}
    </a>
  </motion.div>
);

const ScoreSummary = ({ data }) => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
    <Reveal className="av-card p-8 flex items-center justify-center bg-white">
      <ScoreGauge score={data.visibility_score} rating={data.rating} size={220} />
    </Reveal>
    <Reveal delay={0.1} className="lg:col-span-2 av-card p-8 flex flex-col justify-center">
      <SectionLabel>{t("results.verdict")}</SectionLabel>
      <p className="text-lg md:text-xl leading-relaxed text-obsidian" data-testid="result-summary">{HE ? heSummary(data) : data.summary}</p>
    </Reveal>
  </div>
);

const MetricsGrid = ({ metrics }) => (
  <StaggerGroup className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
    {[[t("results.sov"), metrics.share_of_voice], [t("results.citation"), metrics.citation_rate], [t("results.presence"), metrics.answer_presence], [t("results.sentiment"), metrics.sentiment]].map(([label, value]) => (
      <StaggerItem key={label}><MetricTile label={label} value={value} /></StaggerItem>
    ))}
  </StaggerGroup>
);

const ChartsRow = ({ data }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
    <Reveal className="av-card p-6">
      <SectionLabel>{t("results.chartPlatform")}</SectionLabel>
      <PlatformChart platforms={data.platforms} />
    </Reveal>
    <Reveal delay={0.1} className="av-card p-6">
      <SectionLabel>{t("results.chartTrend")}</SectionLabel>
      <TrendChart trend={data.trend} />
    </Reveal>
  </div>
);

const SnippetsSection = ({ snippets }) => (
  <div className="mb-6">
    <SectionLabel>{t("results.snippets")}</SectionLabel>
    <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {snippets.map((s) => (
        <StaggerItem key={`${s.platform}-${s.query}`}><SnippetCard s={s} /></StaggerItem>
      ))}
    </StaggerGroup>
  </div>
);

const CountdownPill = ({ countdown }) => (
  <div className="fixed bottom-5 inset-x-0 z-[60] flex justify-center pointer-events-none px-4">
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center gap-2.5 bg-obsidian text-white border-2 border-conversion px-5 py-3 shadow-[6px_6px_0px_0px_rgba(255,69,0,0.6)]"
      data-testid="preview-countdown"
    >
      <Unlock className="h-4 w-4 text-conversion" />
      <span className="font-data text-[11px] sm:text-xs font-bold uppercase tracking-[0.18em]">
        {HE ? (
          <>תצוגה חינם — ננעל בעוד <span className="text-conversion text-sm" dir="ltr">{countdown}</span> שניות</>
        ) : (
          <>Free preview — locks in <span className="text-conversion text-sm">{countdown}s</span></>
        )}
      </span>
    </motion.div>
  </div>
);


const heSummary = (d) => {
  const b = d.brand, sc = d.visibility_score;
  if (d.rating === "Low")
    return b + " כמעט בלתי נראה במנועי התשובות של ה-AI. עם ציון " + sc + "/100 — כשלקוחות שואלים את ה-AI מי מומלץ, הם פשוט לא רואים אתכם.";
  if (d.rating === "Medium")
    return "ל-" + b + " יש נוכחות חלקית בחיפוש ה-AI (" + sc + "/100). אתם מופיעים בחלק מהתשובות — אבל מפסידים לקוחות בדיוק במקומות החשובים.";
  return b + " נראה טוב בחיפוש ה-AI (" + sc + "/100) — עכשיו המטרה היא לנעול את המקום הראשון לפני המתחרים.";
};

export const ResultsPreview = ({ preview, analysisId, campaign }) => {
  const [full, setFull] = useState(null);
  const [leadId, setLeadId] = useState(null);
  const [phase, setPhase] = useState("preview"); // preview | locked | unlocked
  const [countdown, setCountdown] = useState(PREVIEW_SECONDS);
  const unlocked = !!full;
  const data = full || preview;
  const issues = (preview.locked?.weaknesses ?? 3) + (preview.locked?.aeo_fixes ?? 4);

  useEffect(() => {
    if (phase !== "preview") return;
    const iv = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(iv);
          setPhase("locked");
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [phase]);

  const onUnlocked = (res) => {
    setFull(res.report);
    setLeadId(res.lead_id);
    setPhase("unlocked");
    setTimeout(() => {
      document.getElementById("full-report")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
  };

  const locked = phase === "locked" && !unlocked;

  return (
    <section id="results" data-testid="results" className="border-t-2 border-obsidian bg-[#F9F9F9] py-16 md:py-24 scroll-mt-16">
      <div className="av-container">
        <ReportHeader domain={data.domain} />
        {unlocked && <UnlockedBanner leadId={leadId} />}

        <div
          className={`transition-all duration-700 ${locked ? "blur-[14px] pointer-events-none select-none" : ""}`}
          aria-hidden={locked}
          data-testid="results-data-zone"
        >
          <ScoreSummary data={data} />
          <MetricsGrid metrics={data.metrics} />
          <ChartsRow data={data} />
          <SnippetsSection snippets={data.snippets} />
          {unlocked && <FullInsights full={full} />}
        </div>

        {phase === "preview" && !unlocked && <CountdownPill countdown={countdown} />}

        {locked && (
          <LockGateModal
            domain={data.domain}
            issues={issues}
            lockedCounts={preview.locked}
            analysisId={analysisId}
            campaign={campaign}
            onUnlocked={onUnlocked}
          />
        )}
      </div>
    </section>
  );
};

const InsightList = ({ icon: Icon, title, items }) => (
  <div className="av-card p-6">
    <div className="flex items-center gap-2 mb-4">
      <Icon className="h-5 w-5 text-conversion" />
      <h3 className="font-heading text-lg font-bold text-obsidian">{title}</h3>
    </div>
    <ul className="space-y-3">
      {items.map((it, i) => (
        <li key={it} className="flex gap-3 text-sm text-neutral-800 leading-relaxed">
          <span className="font-data text-conversion shrink-0">{String(i + 1).padStart(2, "0")}</span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

const CompetitorBenchmark = ({ full }) => {
  const maxScore = Math.max(full.visibility_score, ...full.competitors.map((c) => c.score));
  return (
    <div className="av-card p-6">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="h-5 w-5 text-conversion" />
        <h3 className="font-heading text-lg font-bold text-obsidian">{t("results.benchmark")}</h3>
      </div>
      <div className="space-y-3">
        {[{ name: `${full.brand} ${t("results.you")}`, score: full.visibility_score, you: true }, ...full.competitors].map((c) => (
          <div key={c.name} className="flex items-center gap-3">
            <span className={`w-40 shrink-0 text-sm ${c.you ? "font-bold text-obsidian" : "text-neutral-600"}`}>{c.name}</span>
            <div className="flex-1 bg-neutral-100 h-6 relative">
              <motion.div
                className="h-6"
                style={{ background: c.you ? "#FF4500" : "#0A0A0A" }}
                initial={{ width: 0 }}
                whileInView={{ width: `${(c.score / maxScore) * 100}%` }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            <span className="font-data font-bold w-8 text-right text-obsidian">{c.score}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AeoFixes = ({ fixes }) => (
  <div className="av-card p-6">
    <div className="flex items-center gap-2 mb-4">
      <Wrench className="h-5 w-5 text-conversion" />
      <h3 className="font-heading text-lg font-bold text-obsidian">{t("results.fixes")}</h3>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {fixes.map((fx) => (
        <div key={fx.title} className="border-2 border-[rgba(10,10,10,0.1)] p-4">
          <div className="flex items-center justify-between mb-1">
            <span className="font-heading font-bold text-obsidian">{fx.title}</span>
            <span
              className="font-data text-[10px] font-bold uppercase px-2 py-0.5 border"
              style={{ color: fx.impact === "High" ? "#FF4500" : "#525252", borderColor: fx.impact === "High" ? "#FF4500" : "#525252" }}
            >
              {impactLabel(fx.impact)}
            </span>
          </div>
          <p className="text-sm text-neutral-700">{fx.detail}</p>
        </div>
      ))}
    </div>
  </div>
);

const CountryBreakdown = ({ countries }) => (
  <div className="av-card p-6">
    <div className="flex items-center gap-2 mb-4">
      <Quote className="h-5 w-5 text-conversion" />
      <h3 className="font-heading text-lg font-bold text-obsidian">{t("results.countries")}</h3>
    </div>
    <div className="space-y-3">
      {countries.map((c) => (
        <div key={c.country} className="flex items-center gap-3">
          <span className="w-32 shrink-0 text-sm text-neutral-700">{c.country}</span>
          <div className="flex-1 bg-neutral-100 h-4">
            <motion.div
              className="h-4 bg-obsidian"
              initial={{ width: 0 }}
              whileInView={{ width: `${c.visibility}%` }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            />
          </div>
          <span className="font-data font-bold w-8 text-right text-sm text-obsidian">{c.visibility}</span>
        </div>
      ))}
    </div>
  </div>
);

const FullInsights = ({ full }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="full-insights">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <InsightList icon={AlertTriangle} title={t("results.weaknesses")} items={full.weaknesses} />
      <InsightList icon={Target} title={t("results.opportunities")} items={full.opportunities} />
    </div>

    <AeoFixes fixes={full.aeo_fixes} />
    <CompetitorBenchmark full={full} />

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="av-card p-6">
        <SectionLabel>{t("results.allSnippets")}</SectionLabel>
        <div className="space-y-3 mt-4">
          {full.snippets.map((s) => <SnippetCard key={`${s.platform}-${s.query}`} s={s} />)}
        </div>
      </div>
      <CountryBreakdown countries={full.countries} />
    </div>
  </motion.div>
);

export default ResultsPreview;
